<template>
    <BaseModal :title="'Add Subscription'" @close="closeModal" :visible="true" size="small">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-10">
                <div class="rlms-flex rlms-flex-col rlms-gap-20">
                    <span class="rlms-text-danger-500 rlms-fs-xxs error-text"></span>
                    <div class="popup-scrolled-y rlms-flex rlms-flex-col rlms-gap-20" style="max-height: 300px;">
                        <input type="hidden" v-model.trim="learner_id">
                        <span class="rlms-text-danger-500 rlms-fs-xxs error-text" v-if="submitted && learner_idError">{{
                            learner_idError }}</span>
                        <label class="rlms-us-none rlms-flex rlms-items-center rlms-gap-10">
                            <input id="product" type="radio" :value="subscription?.id" class="selected-product"
                                v-model.trim="subscription_id">
                            <span>{{ subscription?.product?.name }}</span>
                        </label>
                        <span>Quantity: <b>{{ subscription?.left_learner_quantity ?? 0 }}</b></span>
                        <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                            v-if="submitted && subscription_idError">{{ subscription_idError }}</span>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">Cancel</button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn"
                    @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref, watch } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import axios from '@/services/axios';
import { showToast } from '@/utils/swal';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';

const loader = useLoaderStore();

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
    learnerId: {
        type: Number,
        required: true,
    },
    subscription: {
        type: Object,
        required: true,
    },
    // When true, assign via the site-admin endpoint (admin acting on the org's
    // behalf) instead of the org-user endpoint.
    admin: {
        type: Boolean,
        default: false,
    },
});

if (!props.organizationId || typeof props.organizationId !== 'number') {
    throw new Error('organizationId prop is required');
}

if (!props.subscription || typeof props.subscription !== 'object') {
    throw new Error('subscription prop is required');
}

if (!props.learnerId || typeof props.learnerId !== 'number') {
    throw new Error('learnerId prop is required');
}

const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    subscription_id: yup.number().required('Please select the subscription'),
    learner_id: yup.number().required(),
});

// Setup form with schema
const { handleSubmit, setErrors, setFieldValue } = useForm({
    validationSchema: formSchema,
    initialValues: {
        subscription_id: null,
        learner_id: null,
    }
})

// Fields
const { value: subscription_id, errorMessage: subscription_idError } = useField('subscription_id');
const { value: learner_id, errorMessage: learner_idError } = useField('learner_id');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();
            const endpoint = props.admin
                ? `/admin/organizations/${props.organizationId}/assign-subscription`
                : `/organizations/${props.organizationId}/assign-subscription`;
            await axios.post(endpoint, { ...values });

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: 'Subscription assigned successfully!',
            });

            emit('saved');
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};

// Watch for prop change and populate form
watch(
    () => ({ subscription: props.subscription, learnerId: props.learnerId }),
    (newValues) => {
        if (newValues.subscription) {
            // setFieldValue('subscription_id', newValues.subscription?.id || null);
        }

        if (newValues.learnerId) {
            setFieldValue('learner_id', newValues.learnerId || null);
        }
    },
    { immediate: true }
);
</script>
